import { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface OnboardingStep {
 id: number;
 title: string;
 description: string;
}

interface ImprovedOnboardingLayoutProps {
 children: ReactNode;
 currentStep: number;
 totalSteps: number;
 onNext?: () => void;
 onPrevious?: () => void;
 onExit?: () => void;
 isNextDisabled?: boolean;
 isLoading?: boolean;
 nextButtonText?: string;
 showSkip?: boolean;
 onSkip?: () => void;
 onSave?: () => Promise<void>;
 onComplete?: () => void;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
 {
  id: 1,
  title: 'Resume Upload',
  description: 'Upload your resume to extract your professional information',
 },
 {
  id: 2,
  title: 'Review Profile',
  description: 'Review and complete your extracted profile information',
 },
 {
  id: 3,
  title: 'Goals & Preferences',
  description: 'Set your career goals and interview preferences',
 },
];

export function ImprovedOnboardingLayout({
 children,
 currentStep,
 onNext,
 onPrevious,
 isNextDisabled = false,
 isLoading = false,
 nextButtonText = 'Continue',
 showSkip = false,
 onSkip,
}: ImprovedOnboardingLayoutProps) {
 const currentStepInfo = ONBOARDING_STEPS.find(
  (step) => step.id === currentStep
 );

 return (
  <div className='h-full flex flex-col'>
   {/* Step header */}
   {currentStepInfo && (
    <div className='text-center mb-8'>
     <h2 className='text-3xl font-bold text-primary mb-2 font-mono uppercase tracking-wider'>
      {currentStepInfo.title}
     </h2>
     <p className='text-muted-foreground font-mono text-sm'>
      &gt; {currentStepInfo.description.toLowerCase()}
     </p>
    </div>
   )}

   {/* Step content */}
   <div className='flex-1 mb-8 w-full max-w-full overflow-x-hidden'>
    {children}
   </div>

   {/* Navigation */}
   <div className='flex items-center justify-between mt-auto pt-8 border-t border-border bg-background'>
    <div>
     {currentStep > 1 && (
      <Button
       variant='outline'
       onClick={onPrevious}
       disabled={isLoading}
       className='min-w-[100px] border-primary/20 hover:border-primary/50 hover:bg-primary/10 rounded-none font-mono uppercase tracking-wide text-xs'
      >
       <ChevronLeft className='h-4 w-4 mr-2' />
       Previous
      </Button>
     )}
    </div>

    <div className='flex items-center space-x-3'>
     {showSkip && onSkip && (
      <Button
       variant='ghost'
       onClick={onSkip}
       disabled={isLoading}
       className='text-muted-foreground hover:text-primary rounded-none font-mono uppercase tracking-wide text-xs'
      >
       Skip_Step
      </Button>
     )}

     {onNext && (
      <Button
       onClick={onNext}
       disabled={isNextDisabled || isLoading}
       className='min-w-[120px] bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-wide text-xs'
      >
       {isLoading ? (
        <>
         <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
         PROCESSING...
        </>
       ) : (
        <>
         {nextButtonText.toUpperCase()}
         <ChevronRight className='h-4 w-4 ml-2' />
        </>
       )}
      </Button>
     )}
    </div>
   </div>
  </div>
 );
}
